/**
 * Delivery Rate Service
 * Handles delivery rate calculations based on seller and buyer locations
 * Uses Delevery_rate.json for rate matrix and city data
 */

import deliveryRateData from '../Delevery_rate.json';
import { secureLog } from '../lib/errors/logger';
import { sanitizeError } from '../utils/sanitization';

export type TerrainType = 'KATHMANDU' | 'TERAI' | 'PAHAD' | 'HIMAL';

interface DeliveryRateData {
  meta: {
    currency: string;
    base_weight_kg: number;
    vat_inclusive: boolean;
    purpose: string;
    logic: string;
  };
  rate_matrix: {
    KATHMANDU: Record<TerrainType, number>;
    TERAI: Record<TerrainType, number>;
    PAHAD: Record<TerrainType, number>;
    HIMAL: Record<TerrainType, number>;
  };
  cities: Array<{
    State_L1_Districts_of_Nepal: string;
    City_L2_Municipalities_of_Nepal: string;
    L3_address_level: string;
    Delivery_service: string;
    COD_Service: string;
    Transit_Hub: string;
    HUB_CODE: string;
    Province: number;
    Terai_Pahad_Himal: string;
    rate: Record<string, any>;
    pricing_rules: {
      base_rate_upto_2kg_same_province: number;
      additional_per_kg_after_2kg: number;
      cross_province_fee_per_province: number;
      vat_inclusive: boolean;
    };
  }>;
}

interface CityData {
  city: string;
  district: string;
  province: number;
  terrain: TerrainType;
  pricingRules: {
    base_rate_upto_2kg_same_province: number;
    additional_per_kg_after_2kg: number;
    cross_province_fee_per_province: number;
    vat_inclusive: boolean;
  };
}

class DeliveryRateService {
  private rateData: DeliveryRateData;
  private cityCache: Map<string, CityData> = new Map();
  private districtCache: Map<string, CityData[]> = new Map();

  constructor() {
    this.rateData = deliveryRateData as DeliveryRateData;
    this.buildCityCache();
  }

  /**
   * Build cache for fast city lookups
   */
  private buildCityCache(): void {
    try {
      for (const cityEntry of this.rateData.cities) {
        const cityName = cityEntry.City_L2_Municipalities_of_Nepal.toLowerCase().trim();
        const districtName = cityEntry.State_L1_Districts_of_Nepal.toLowerCase().trim();
        
        // Normalize terrain
        const terrain = this.normalizeTerrain(cityEntry.Terai_Pahad_Himal);
        
        const cityData: CityData = {
          city: cityEntry.City_L2_Municipalities_of_Nepal,
          district: cityEntry.State_L1_Districts_of_Nepal,
          province: cityEntry.Province,
          terrain,
          pricingRules: cityEntry.pricing_rules,
        };

        // Cache by city name (lowercase)
        if (!this.cityCache.has(cityName)) {
          this.cityCache.set(cityName, cityData);
        }

        // Cache by district for fallback lookup
        if (!this.districtCache.has(districtName)) {
          this.districtCache.set(districtName, []);
        }
        this.districtCache.get(districtName)!.push(cityData);
      }
    } catch (error) {
      secureLog('error', 'Failed to build city cache:', sanitizeError(error));
    }
  }

  /**
   * Normalize terrain string to TerrainType
   */
  private normalizeTerrain(terrain: string): TerrainType {
    const normalized = terrain?.toUpperCase().trim();
    
    // Check for Kathmandu (special case - it's a city, not a terrain)
    if (normalized === 'KATHMANDU') {
      return 'KATHMANDU';
    }
    
    // Map common variations
    if (normalized === 'TERAI' || normalized === 'TARAI') {
      return 'TERAI';
    }
    if (normalized === 'PAHAD' || normalized === 'PAHAR' || normalized === 'HILL') {
      return 'PAHAD';
    }
    if (normalized === 'HIMAL' || normalized === 'HIMALAYA' || normalized === 'MOUNTAIN') {
      return 'HIMAL';
    }
    
    // Default fallback
    return 'TERAI';
  }

  /**
   * Resolve city/district to terrain type
   * @param city City name
   * @param district Optional district name for fallback
   * @returns Terrain type
   */
  resolveCityToTerrain(city: string, district?: string): TerrainType {
    if (!city || typeof city !== 'string') {
      secureLog('warn', 'Invalid city name provided to resolveCityToTerrain', { city });
      return 'TERAI'; // Default fallback
    }

    const cityName = city.toLowerCase().trim();
    
    // Try direct city lookup
    const cityData = this.cityCache.get(cityName);
    if (cityData) {
      return cityData.terrain;
    }

    // Try district lookup as fallback
    if (district) {
      const districtName = district.toLowerCase().trim();
      const districtCities = this.districtCache.get(districtName);
      if (districtCities && districtCities.length > 0) {
        // Use first city in district (most common terrain)
        return districtCities[0].terrain;
      }
    }

    // Special case: Check if city contains "kathmandu" or "lalitpur" or "bhaktapur"
    if (cityName.includes('kathmandu') || cityName.includes('lalitpur') || cityName.includes('bhaktapur')) {
      return 'KATHMANDU';
    }

    // Default fallback
    secureLog('warn', 'City not found in rate data, using TERAI as fallback', { city, district });
    return 'TERAI';
  }

  /**
   * Get city data including province and pricing rules
   */
  getCityData(city: string, district?: string): CityData | null {
    if (!city || typeof city !== 'string') {
      return null;
    }

    const cityName = city.toLowerCase().trim();
    const cityData = this.cityCache.get(cityName);
    if (cityData) {
      return cityData;
    }

    // Try district lookup
    if (district) {
      const districtName = district.toLowerCase().trim();
      const districtCities = this.districtCache.get(districtName);
      if (districtCities && districtCities.length > 0) {
        return districtCities[0];
      }
    }

    return null;
  }

  /**
   * Get province number from city/district
   */
  getProvince(city: string, district?: string): number | null {
    const cityData = this.getCityData(city, district);
    return cityData?.province ?? null;
  }

  /**
   * Calculate shipment cost based on seller and buyer locations
   */
  calculateShipmentCost(params: {
    sellerTerrain: TerrainType;
    buyerTerrain: TerrainType;
    sellerProvince: number;
    buyerProvince: number;
    weightKg: number;
    pricingRules?: {
      base_rate_upto_2kg_same_province?: number;
      additional_per_kg_after_2kg?: number;
      cross_province_fee_per_province?: number;
    };
  }): number {
    const {
      sellerTerrain,
      buyerTerrain,
      sellerProvince,
      buyerProvince,
      weightKg,
      pricingRules,
    } = params;

    // Validate inputs
    if (!sellerTerrain || !buyerTerrain) {
      secureLog('warn', 'Invalid terrain provided to calculateShipmentCost', { sellerTerrain, buyerTerrain });
      return 150; // Default fallback
    }

    if (!Number.isFinite(sellerProvince) || !Number.isFinite(buyerProvince)) {
      secureLog('warn', 'Invalid province provided to calculateShipmentCost', { sellerProvince, buyerProvince });
      return 150; // Default fallback
    }

    // Validate weight
    const sanitizedWeight = Math.max(0, Math.min(weightKg || 2, 1000)); // Cap at 1000kg for security

    // Get base rate from rate matrix
    const rateMatrix = this.rateData.rate_matrix;
    const baseRate = rateMatrix[sellerTerrain]?.[buyerTerrain];
    
    if (!Number.isFinite(baseRate)) {
      secureLog('warn', 'Base rate not found in rate matrix', { sellerTerrain, buyerTerrain });
      return 150; // Default fallback
    }

    // Use pricing rules if provided, otherwise use defaults from meta
    const baseRateUpto2kg = pricingRules?.base_rate_upto_2kg_same_province ?? baseRate;
    const additionalPerKg = pricingRules?.additional_per_kg_after_2kg ?? 100;
    const crossProvinceFee = pricingRules?.cross_province_fee_per_province ?? 15;

    // Calculate province difference
    const provinceDiff = Math.abs(sellerProvince - buyerProvince);
    const crossProvinceCost = provinceDiff > 0 ? provinceDiff * crossProvinceFee : 0;

    // Calculate weight-based cost
    let weightCost = baseRateUpto2kg;
    if (sanitizedWeight > 2) {
      const extraKg = sanitizedWeight - 2;
      weightCost = baseRateUpto2kg + (extraKg * additionalPerKg);
    }

    // Total cost = weight cost + cross-province fee
    const totalCost = weightCost + crossProvinceCost;

    // Security: Cap at reasonable maximum (50,000 NPR)
    return Math.min(totalCost, 50000);
  }

  /**
   * Get rate matrix for reference
   */
  getRateMatrix(): DeliveryRateData['rate_matrix'] {
    return this.rateData.rate_matrix;
  }

  /**
   * Check if city supports delivery service
   */
  supportsDelivery(city: string, district?: string): boolean {
    const cityData = this.getCityData(city, district);
    if (!cityData) {
      return true; // Default to true if city not found
    }

    // Check delivery service from original data
    const cityEntry = this.rateData.cities.find(
      (c) => c.City_L2_Municipalities_of_Nepal.toLowerCase() === city.toLowerCase()
    );
    
    return cityEntry?.Delivery_service === 'Yes';
  }

  /**
   * Check if city supports COD service
   */
  supportsCOD(city: string, district?: string): boolean {
    const cityData = this.getCityData(city, district);
    if (!cityData) {
      return true; // Default to true if city not found
    }

    // Check COD service from original data
    const cityEntry = this.rateData.cities.find(
      (c) => c.City_L2_Municipalities_of_Nepal.toLowerCase() === city.toLowerCase()
    );
    
    return cityEntry?.COD_Service === 'Yes';
  }
}

// Singleton instance
let deliveryRateServiceInstance: DeliveryRateService | null = null;

/**
 * Get delivery rate service instance (singleton)
 */
export function getDeliveryRateService(): DeliveryRateService {
  if (!deliveryRateServiceInstance) {
    deliveryRateServiceInstance = new DeliveryRateService();
  }
  return deliveryRateServiceInstance;
}

// Export default instance
export const deliveryRateService = getDeliveryRateService();

